import { z } from 'zod/v4'
import { paginationLinksSchema, paginationMetaSchema } from '@/lib/schema/v2/pagination'
import { userSchema } from './user'
import { ticketSchema } from '@/lib/schema/v2/ticket'
import { zDayJs } from '@/lib/schema/v2/dayjs'
import { ampApiErrorSchema } from '@/lib/schema/v2/amp-api-response'

export const fieldsForTable =
    'uuid,ticket_uuid,is_primary,assignee_uuid,assignee_type,assignee{uuid,full_name},status,start_at,end_at,time_spent_in_seconds'

export const fieldsForEdit =
    'uuid,ticket_uuid,is_primary,assignee_uuid,assignee_type,assignee{uuid,first_name,last_name},status,start_at,end_at,time_spent_in_seconds,notes'

export const ticketResourceSchema = z.object({
    assignee: userSchema.nullish(),
    assignee_type: z.string().nullish(),
    assignee_uuid: z.uuid().nullish(),
    charge: z.number().nullish(),
    company_site_uuid: z.uuid().nullish(),
    cost: z.number().nullish(),
    created_at: zDayJs.nullish(),
    creator: userSchema.nullish(),
    creator_type: z.string().nullish(),
    creator_uuid: z.uuid().nullish(),
    description: z.string().nullish(),
    end_at: zDayJs.nullish(),
    is_primary: z.boolean().nullish(),
    is_ready_for_billing: z.boolean().nullish(),
    job_description: z.string().nullish(),
    location_type: z.string().nullish(),
    notes: z.string().nullish(),
    rate_uuid: z.uuid().nullish(),
    start_at: zDayJs.nullish(),
    status: z.string().nullish(),
    tariff_regex_signature: z.string().nullish(),
    ticket: z.lazy(() => ticketSchema.nullish()),
    ticket_uuid: z.uuid().nullish(),
    time_spent_in_seconds: z.number().nullish(),
    updated_at: zDayJs.nullish(),
    uuid: z.uuid().nullish(),
    work_performed_description: z.string().nullish(),
    work_type: z.string().nullish(),
})

export const ticketResourcesSchema = z.array(ticketResourceSchema)

export const paginatedTicketResourcesResponseSchema = z.object({
    data: ticketResourcesSchema.nullish(),
    links: paginationLinksSchema.nullish(),
    meta: paginationMetaSchema.nullish(),
    success: z.boolean(),
    error: ampApiErrorSchema.nullish(),
})

export const retrieveTicketResourceResponseSchema = z.object({
    data: ticketResourceSchema.nullish(),
    success: z.boolean(),
    error: ampApiErrorSchema.nullish(),
})

export const createTicketResourceResponseSchema = z.object({
    data: ticketResourceSchema.nullish(),
    success: z.boolean(),
    error: ampApiErrorSchema.nullish(),
})

export const deleteTicketResourceResponseSchema = z.object({
    data: ticketResourceSchema.nullish(),
    success: z.boolean(),
    error: ampApiErrorSchema.nullish(),
})

export const createTicketResourceFormSchema = z
    .object({
        assignee: userSchema.optional().default(null),
        ticket_uuid: z.uuid().optional(),
        is_primary: z.boolean().optional(),
        status: z.string().default('in-progress'),
        start_at: z.iso.datetime({ local: true }).nullish(),
        end_at: z.iso.datetime({ local: true }).nullish(),
        notes: z.string().nullish(),
    })
    .required({
        assignee: true,
    })

export const editTicketResourceFormSchema = z
    .object({
        assignee: userSchema.optional().default(null),
        ticket_uuid: z.uuid().optional(),
        is_primary: z.boolean().optional(),
        status: z.string().default('in-progress'),
        start_at: z.iso.datetime({ local: true }).nullish(),
        end_at: z.iso.datetime({ local: true }).nullish(),
        notes: z.string().nullish(),
    })
    .required({
        assignee: true,
    })

export type PaginatedTicketResourcesResponse = z.infer<
    typeof paginatedTicketResourcesResponseSchema
>
export type TicketResource = z.infer<typeof ticketResourceSchema>
export type TicketResources = z.infer<typeof ticketResourcesSchema>
export type CreateTicketResourceForm = z.infer<typeof createTicketResourceFormSchema>
export type RetrieveTicketResourceResponse = z.infer<typeof retrieveTicketResourceResponseSchema>
export type DeleteTicketResourceResponse = z.infer<typeof deleteTicketResourceResponseSchema>
export type CreateTicketResourceResponse = z.infer<typeof createTicketResourceResponseSchema>
export type EditTicketResourceForm = z.infer<typeof editTicketResourceFormSchema>
